import {
  type FileHandler as TTFileHandler,
  type FileHandlePluginOptions as Options,
} from "@tiptap-pro/extension-file-handler";
import { startUpload } from "./uploadFiles";

import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import type { EditorView } from "@tiptap/pm/view";
import { uploadFileToUploadcare } from "app/utils/uploadcare";
import { Size } from "./blocks/shared/attributes";
import { alerts } from "app/stores/alert";

export interface FileHandlerOptions {
  community: string;
  user: string;
}

function replaceImage(
  currentUrl: string,
  newUrl: { src: string; type: string; uuid: string },
  view: EditorView
) {
  // finds the image by it's url and replaces it with the new url and sets
  // up the attributes based on file info
  view.state.doc.descendants((node, pos) => {
    if (node.type.name === "image" && node.attrs.src === currentUrl) {
      const tr = view.state.tr
        .setNodeAttribute(pos, "src", newUrl.src)
        .setNodeAttribute(pos, "type", newUrl.type)
        .setNodeAttribute(pos, "isUploading", false)
        .setNodeAttribute(pos, "uuid", newUrl.uuid);
      view.dispatch(tr);
    }
  });
}

function deleteImage(currentUrl: string, view: EditorView) {
  view.state.doc.descendants((node, pos) => {
    if (node.type.name === "image" && node.attrs.src === currentUrl) {
      const tr = view.state.tr.scrollIntoView().delete(pos, pos + 1);
      view.dispatch(tr);
    }
  });
}
async function uploadAndReplaceImages(imageUrls: string[], view: EditorView) {
  // set placeholder attribute first
  for (const url of imageUrls) {
    view.state.doc.descendants((node, pos) => {
      if (node.type.name === "image" && node.attrs.src === url) {
        const tr = view.state.tr
          .setNodeAttribute(pos, "isUploading", true)
          .setNodeAttribute(pos, "width", Size.SMALL);
        view.dispatch(tr);
      }
    });
  }

  // upload images and replace placeholders
  let i = 0;
  const alert = alerts.progress(`Uploading ${imageUrls.length} images`, 0);
  for (const url of imageUrls) {
    i += 1;
    try {
      const newUrl = await uploadFileToUploadcare(url, true);
      replaceImage(url, newUrl, view);
    } catch {
      deleteImage(url, view);
      alerts.error(`Failed to upload image ${i}/${imageUrls.length}`);
    }
    if (imageUrls.length > 1) {
      alert.update({
        type: "progress",
        title: `Uploaded image ${i}/${imageUrls.length}`,
        progress: Math.floor((i / imageUrls.length) * 100),
      });
    }
  }
  if (alert)
    setTimeout(
      () => alert?.update({ type: "success", title: `Uploaded image ${i}/${imageUrls.length}` }),
      500
    );
}

// temporarily vendorised and modified from tiptap-pro/extension-file-handler to override behaviour of onPaste
// when pasting html content
const FileHandlePlugin = ({ key, editor, onPaste, onDrop, allowedMimeTypes: n }: Options) =>
  new Plugin({
    key: key || new PluginKey("fileHandler"),
    props: {
      handleDrop(view, event) {
        let data;
        if (!onDrop) return !1;
        if (!(null === (data = event.dataTransfer) || void 0 === data ? void 0 : data.files.length))
          return !1;
        const i = view.posAtCoords({ left: event.clientX, top: event.clientY });
        let l = Array.from(event.dataTransfer?.files || []);
        return (
          n && (l = l.filter((e) => n.includes(e.type))),
          0 !== l.length &&
            (event.preventDefault(),
            event.stopPropagation(),
            onDrop(editor, l, (null == i ? void 0 : i.pos) || 0),
            !0)
        );
      },
      handlePaste(view, event, slice) {
        let data;
        if (!onPaste) return !1;

        // no clipboard data, check slice for imgs instead
        if (
          !(null === (data = event.clipboardData) || void 0 === data ? void 0 : data.files.length)
        ) {
          const imageUrls: string[] = [];
          slice.content.descendants((node) => {
            if (node.type.name === "image") {
              imageUrls.push(node.attrs.src);
            }
          });

          // found images, schedule upload and replace once current view rendered
          if (imageUrls.length) {
            setTimeout(() => {
              uploadAndReplaceImages(imageUrls, view);
            }, 100);
          }
          return false;
        }
        // got clipboard data, get files and pass to handler
        let a = Array.from(event.clipboardData?.files || []);
        const l = event.clipboardData?.getData("text/html");
        // overriden here to prevent pasteRules from running after
        // processing html content in handler that returns true
        return (
          n && (a = a.filter((e) => n.includes(e.type))),
          0 !== a.length && (event.preventDefault(), event.stopPropagation(), onPaste(editor, a, l))
        );
      },
    },
  });

const TipTapProFileHandler: typeof TTFileHandler = Extension.create({
  name: "fileHandler",
  addOptions: () => ({ onPaste: void 0, onDrop: void 0, allowedMimeTypes: void 0 }),
  addProseMirrorPlugins() {
    return [
      FileHandlePlugin({
        key: new PluginKey(this.name),
        editor: this.editor,
        allowedMimeTypes: this.options.allowedMimeTypes,
        onDrop: this.options.onDrop,
        onPaste: this.options.onPaste,
      }),
    ];
  },
});

export const FileHandler = (metadata: FileHandlerOptions) =>
  TipTapProFileHandler.configure({
    onDrop(editor, files, pos) {
      let i = 0;
      const alert = alerts.progress(`Uploading ${files.length} images`, 0);
      files.forEach(async (file) => {
        await startUpload(file, editor.view, pos, metadata);
        i++;
        alert.update({
          type: "progress",
          title: `Uploaded image ${i}/${files.length}`,
          progress: Math.floor((i / files.length) * 100),
        });
      });
    },
    onPaste(editor, files, htmlContent) {
      if (htmlContent) {
        // if there is htmlContent in the clipboard and it has a src don't allow normal paste
        // we want to always ensure content is hosted on uploadcare to prevent dead hotlinks to content
        if (!htmlContent.includes("src=")) {
          return false;
        }
      }

      // insert at the current cursor position
      let i = 0;
      const alert = alerts.progress(`Uploading ${files.length} images`, 0);
      files.forEach(async (file) => {
        await startUpload(file, editor.view, editor.state.selection.anchor, metadata);
        i++;
        alert.update({
          type: "progress",
          title: `Uploaded image ${i}/${files.length}`,
          progress: Math.floor((i / files.length) * 100),
        });
      });
      return true;
    },
  });
